import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Figures Cards count-up (bskr/layout-figures-cards). Global partial — the
// figure cards are nested inside the layout, where per-block scripts aren't
// reliably enqueued. Each `.bskr-figure-card__figure` heading holds a plain
// figure typed by the editor (e.g. "+340", "+2000", "6000 m²"); we split it
// into prefix / number / suffix and count the number from 0 to its target when
// the card scrolls into view.
//
// Editor: skipped (wp.data only exists in Gutenberg). Reduced motion: the final
// value is left untouched, no animation. ScrollTrigger is already synced with
// Lenis in main.js.

const FIGURE_RE = /^(\D*)(\d[\d.,]*)(\D*)$/;

function start() {
  if (typeof window !== "undefined" && window.wp && window.wp.data) return;
  init();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}

function init() {
  const roots = document.querySelectorAll(".wp-block-bskr-layout-figures-cards");
  if (!roots.length) return;

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  roots.forEach((root) => {
    if (root.dataset.figuresInited === "1") return;
    root.dataset.figuresInited = "1";

    // Inside a Content Slider slide the vertical scroll trigger can't fire (the
    // slide is translated horizontally), which would freeze the count-up at 0.
    // Render the final figures immediately, same as reduced motion.
    const isStatic = reduce || root.closest("[data-no-scroll-reveal]") !== null;

    root.querySelectorAll(".bskr-figure-card__figure").forEach((figure) => {
      setupFigure(figure, isStatic);
    });
  });
}

function setupFigure(figure, reduce) {
  const original = figure.textContent.trim();
  const match = original.match(FIGURE_RE);

  // No parseable number → leave the text exactly as authored.
  if (!match) return;

  const [, prefix, rawNumber, suffix] = match;

  // Detect the thousands separator from the authored string so the count-up
  // re-formats with the same convention (es-ES uses ".").
  const usesDotThousands = /\d\.\d{3}(\D|$)/.test(rawNumber);
  const target = parseInt(rawNumber.replace(/[.,\s]/g, ""), 10);
  if (!Number.isFinite(target)) return;

  figure.dataset.countOriginal = original;

  const fmt = (n) =>
    usesDotThousands ? Math.round(n).toLocaleString("es-ES") : String(Math.round(n));

  figure.innerHTML = "";
  if (prefix) figure.appendChild(span("bskr-figure-card__figure-prefix", prefix));
  const numEl = span("bskr-figure-card__figure-num", reduce ? fmt(target) : "0");
  numEl.dataset.countTo = String(target);
  figure.appendChild(numEl);
  if (suffix) figure.appendChild(span("bskr-figure-card__figure-suffix", suffix));

  if (reduce) return;

  const proxy = { v: 0 };
  gsap.to(proxy, {
    v: target,
    duration: 1.6,
    ease: "power2.out",
    scrollTrigger: { trigger: figure, start: "top 85%", once: true },
    onUpdate: () => {
      numEl.textContent = fmt(proxy.v);
    },
  });
}

function span(className, text) {
  const el = document.createElement("span");
  el.className = className;
  el.textContent = text;
  return el;
}
